import { useCallback, useContext, useState } from 'react'
import { useIntl } from 'react-intl'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Heading from 'LuxKit/Typography/Heading'
import { ModalElementContext } from 'contexts/ModalProvider'
import type {
  MyTripsFilterOptions,
  MyTripsSortOptions,
} from 'tripPlanner/pages/myTrips/myTripsSortFilterOptions'
import {
  getMyTripsFilterOptionLabel,
  getMyTripsSortOptionLabel,
  MY_TRIPS_FILTER_OPTIONS,
  MY_TRIPS_SORT_OPTIONS,
} from 'tripPlanner/pages/myTrips/myTripsSortFilterOptions'

interface Props {
  onFilterApply: (opt: MyTripsFilterOptions | null) => void
  onSortApply: (opt: MyTripsSortOptions) => void
  selectedSortOption: MyTripsSortOptions
  selectedFilter: MyTripsFilterOptions | null
  isFilterVisible: boolean
}

export default function MyTripsSortAndFilter({
  onFilterApply,
  onSortApply,
  selectedSortOption,
  selectedFilter,
  isFilterVisible,
}: Props) {
  const intl = useIntl()
  const modalElement = useContext(ModalElementContext)
  const [sortOption, setSortOption] =
    useState<MyTripsSortOptions>(selectedSortOption)
  const [filter, setFilter] = useState<MyTripsFilterOptions | null>(
    selectedFilter,
  )

  const onApply = useCallback(() => {
    onFilterApply(filter)
    onSortApply(sortOption)
    modalElement?.resolve()
  }, [modalElement, filter, onFilterApply, onSortApply, sortOption])

  const onClickFilter = useCallback(
    (opt: MyTripsFilterOptions | null) => {
      if (opt === filter) {
        setFilter(null)
      } else {
        setFilter(opt)
      }
    },
    [filter, setFilter],
  )

  const sortAndFilterTitle = intl.formatMessage({
    id: 'PtG7hGmeKKXfWyOndUb8',
    defaultMessage: 'Sort & Filter',
    description: 'Modal title when both sort and filter options are visible',
  })
  const sortByTitle = intl.formatMessage({
    id: 'hTLTKuxNfpGEr29HTAlo',
    defaultMessage: 'Sort by',
    description: 'Modal title when only sort options are visible in the trip planner item editor modal',
  })

  return (
    <>
      <ModalHeader title={isFilterVisible ? sortAndFilterTitle : sortByTitle} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            {isFilterVisible && <Heading variant="heading5">
              {intl.formatMessage({
                id: '45duTriPs5vKulDwA7Q3',
                defaultMessage: 'Sort by',
                description: 'Section heading for sort options in my trips filter modal',
              })}
            </Heading>}
            <VerticalSpacer gap={12}>
              {MY_TRIPS_SORT_OPTIONS.map((opt) => (
                <div key={opt}>
                  <RadioInput
                    onClick={() => setSortOption(opt)}
                    checked={opt === sortOption}
                  >
                    {getMyTripsSortOptionLabel(intl, opt)}
                  </RadioInput>
                </div>
              ))}
            </VerticalSpacer>
            {isFilterVisible && (
              <>
                <Heading variant="heading5">
                  {intl.formatMessage({
                    id: 'kgw_uET8g7N8TE2lm8Ay',
                    defaultMessage: 'Filter by',
                    description: 'Section heading for filter options in my trips filter modal',
                  })}
                </Heading>
                <VerticalSpacer gap={12}>
                  {MY_TRIPS_FILTER_OPTIONS.map((opt) => (
                    <div key={opt}>
                      <RadioInput
                        onClick={() => onClickFilter(opt)}
                        checked={filter === opt}
                      >
                        {getMyTripsFilterOptionLabel(intl, opt)}
                      </RadioInput>
                    </div>
                  ))}
                </VerticalSpacer>
              </>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'A3vBZAE17wtPihRNWIme',
            defaultMessage: 'Apply',
            description: 'Button to apply sort and filter selections to my trips list',
          }),
          onClick: onApply,
        }}
      />
    </>
  )
}
